import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { logger } from '@axon/ui/utils';

import { useAuthStore } from '@/stores/auth.store';

import { handleError } from './error-handler';
import {
  type AxiosRequestConfigWithRetry,
  type RetryConfig,
  isRetryableError,
  calculateRetryDelay,
  sleep,
  canRetry,
  incrementRetryCount,
  mergeRetryConfig,
} from './api-retry-config';

// Extended config type with metadata for request tracking
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: {
    startTime: number;
  };
}

// Queue to hold requests while refreshing token
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// API base URL - use /api/v1 prefix for Next.js rewrite to proxy to backend
const API_BASE_URL = '/api/v1';

// Helper function to get token from cookie (browser-only)
const getTokenFromCookie = (): string | null => {
  if (typeof window === 'undefined') return null;

  const cookieMatch = document.cookie.match(/access-token=([^;]+)/);
  return cookieMatch ? cookieMatch[1] : null;
};

// Global retry configuration
let globalRetryConfig: Partial<RetryConfig> = {};

// Create axios instance with retry configuration
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for httpOnly cookies
});

/**
 * Configure global retry settings for all requests
 * @example
 * configureApiRetry({
 *   maxRetries: 5,
 *   retryDelay: 2000,
 *   retryableStatusCodes: [500, 502, 503, 504]
 * });
 */
export function configureApiRetry(config: Partial<RetryConfig>) {
  globalRetryConfig = config;
}

// Request interceptor
apiClient.interceptors.request.use(
  (config: ExtendedAxiosRequestConfig) => {
    // Add auth token if available - try store first, then cookie as fallback
    let token = useAuthStore.getState().getValidToken();
    if (!token) {
      token = getTokenFromCookie();
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Note: Removed _t cache-busting parameter as React Query handles caching
    // efficiently through its queryKey mechanism

    // Add tenant header if available
    const tenantId = useAuthStore.getState().tenantId;
    if (tenantId) {
      config.headers['X-Tenant-Id'] = tenantId;
    }

    // Note: updateLastActivity was removed as it's not in /store/auth.store.ts
    // This can be added back if needed in the auth store

    // Add request logging
    if (process.env.NEXT_PUBLIC_API_LOGGING === 'true') {
      // Store start time for duration calculation
      config.metadata = { startTime: Date.now() };

      logger.info(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        params: config.params,
        data: config.data,
        timestamp: new Date().toISOString(),
      });
    }

    return config;
  },
  (error) => {
    if (process.env.NEXT_PUBLIC_API_LOGGING === 'true') {
      logger.error('[API Request Error]', error);
    }
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Add response logging
    if (process.env.NEXT_PUBLIC_API_LOGGING === 'true') {
      const config = response.config as ExtendedAxiosRequestConfig;
      const duration = config.metadata?.startTime
        ? Date.now() - config.metadata.startTime
        : undefined;

      logger.info(`[API Response] ${response.status} ${response.config.url}`, {
        status: response.status,
        statusText: response.statusText,
        duration: duration ? `${duration}ms` : 'N/A',
        data: response.data,
        timestamp: new Date().toISOString(),
      });
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfigWithRetry & {
      skipAuthRefresh?: boolean;
    };

    // Skip retry and refresh for certain requests
    if (
      !originalRequest ||
      originalRequest.skipAuthRefresh ||
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/register')
    ) {
      return Promise.reject(error);
    }

    // Get retry configuration (merge global, request-specific, and defaults)
    const retryConfig = mergeRetryConfig({
      ...globalRetryConfig,
      ...originalRequest.retryConfig,
    });

    // Handle retryable errors (network errors, 5xx, etc.) before auth refresh
    if (
      error.response?.status !== 401 && // Don't retry 401s here, handle them separately
      !originalRequest._retry &&
      isRetryableError(error, retryConfig) &&
      canRetry(originalRequest, retryConfig)
    ) {
      const retryCount = incrementRetryCount(originalRequest);
      const delay = calculateRetryDelay(retryCount, retryConfig);

      // Log retry attempt
      logger.warn(
        `Retrying request to ${originalRequest.url} (attempt ${retryCount}/${retryConfig.maxRetries}) after ${delay}ms`
      );

      // Call onRetry callback if provided
      retryConfig.onRetry?.(error, retryCount);

      // Wait before retrying
      await sleep(delay);

      // Retry the request
      return apiClient(originalRequest);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Call the Next.js API route for token refresh
        const response = await fetch('/api/v1/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Token refresh failed');
        }

        const data = await response.json();

        // Update user in store if returned
        if (data.user) {
          useAuthStore.getState().setUser(data.user);
        }

        processQueue(null);

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Clear auth and redirect to login
        useAuthStore.getState().logout();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle all errors with our error handler
    const appError = handleError(error);

    // Log specific error types
    if (error.response?.status === 403) {
      logger.error('Forbidden', error, { message: appError.message, url: error.config?.url });
    }

    if (error.response?.status === 429) {
      logger.error('Rate limit exceeded', error, {
        message: appError.message,
        url: error.config?.url,
      });
    }

    // Add comprehensive error logging
    if (process.env.NEXT_PUBLIC_API_LOGGING === 'true') {
      const errorConfig = error.config as ExtendedAxiosRequestConfig | undefined;
      const duration = errorConfig?.metadata?.startTime
        ? Date.now() - errorConfig.metadata.startTime
        : undefined;

      logger.error(
        `[API Error] ${error.response?.status || 'Network Error'} ${error.config?.url}`,
        error,
        {
          status: error.response?.status,
          statusText: error.response?.statusText,
          duration: duration ? `${duration}ms` : 'N/A',
          errorData: error.response?.data || error.message,
          timestamp: new Date().toISOString(),
        }
      );
    }

    // Reject with the parsed app error for better error handling downstream
    return Promise.reject(appError);
  }
);

// Export typed API client
export default apiClient;

// Export retry configuration utilities
export { DEFAULT_RETRY_CONFIG, type RetryConfig } from './api-retry-config';

// Type-safe API methods
export const api = {
  // Direct access methods
  get: <T = unknown>(url: string, config?: AxiosRequestConfigWithRetry) =>
    apiClient.get<T>(url, config),
  post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfigWithRetry) =>
    apiClient.post<T>(url, data, config),
  patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfigWithRetry) =>
    apiClient.patch<T>(url, data, config),
  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfigWithRetry) =>
    apiClient.put<T>(url, data, config),
  delete: <T = unknown>(url: string, config?: AxiosRequestConfigWithRetry) =>
    apiClient.delete<T>(url, config),

  // Orders
  orders: {
    list: (params?: { page?: number; limit?: number; status?: string }) =>
      apiClient.get('/orders', { params }),
    get: (id: string) => apiClient.get(`/orders/${id}`),
    create: (data: unknown) => apiClient.post('/orders', data),
    update: (id: string, data: unknown) => apiClient.patch(`/orders/${id}`, data),
    delete: (id: string) => apiClient.delete(`/orders/${id}`),
  },

  // Shipments
  shipments: {
    list: (params?: { page?: number; limit?: number; status?: string }) =>
      apiClient.get('/shipments', { params }),
    get: (id: string) => apiClient.get(`/shipments/${id}`),
    create: (data: unknown) => apiClient.post('/shipments', data),
    update: (id: string, data: unknown) => apiClient.patch(`/shipments/${id}`, data),
    delete: (id: string) => apiClient.delete(`/shipments/${id}`),
    updatePOD: (id: string, data: unknown) => apiClient.post(`/shipments/${id}/pod`, data),
  },

  // Customers
  customers: {
    list: (params?: { page?: number; limit?: number }) => apiClient.get('/customers', { params }),
    get: (id: string) => apiClient.get(`/customers/${id}`),
    create: (data: unknown) => apiClient.post('/customers', data),
    update: (id: string, data: unknown) => apiClient.patch(`/customers/${id}`, data),
    delete: (id: string) => apiClient.delete(`/customers/${id}`),
  },

  // Power Units (Equipment module - replaces legacy vehicles)
  powerUnits: {
    list: (params?: { page?: number; limit?: number; category?: string }) =>
      apiClient.get('/power-units', { params }),
    get: (id: string) => apiClient.get(`/power-units/${id}`),
    create: (data: unknown) => apiClient.post('/power-units', data),
    update: (id: string, data: unknown) => apiClient.patch(`/power-units/${id}`, data),
    delete: (id: string) => apiClient.delete(`/power-units/${id}`),
  },

  // Drivers
  drivers: {
    list: (params?: { page?: number; limit?: number; available?: boolean }) =>
      apiClient.get('/drivers', { params }),
    get: (id: string) => apiClient.get(`/drivers/${id}`),
    create: (data: unknown) => apiClient.post('/drivers', data),
    update: (id: string, data: unknown) => apiClient.patch(`/drivers/${id}`, data),
    delete: (id: string) => apiClient.delete(`/drivers/${id}`),
  },

  // Tracking
  tracking: {
    getPowerUnitPosition: (powerUnitId: string) =>
      apiClient.get(`/tracking/power-units/${powerUnitId}/position`),
    updatePosition: (powerUnitId: string, data: unknown) =>
      apiClient.post(`/tracking/power-units/${powerUnitId}/position`, data),
    getHistory: (powerUnitId: string, params?: { start?: string; end?: string }) =>
      apiClient.get(`/tracking/power-units/${powerUnitId}/history`, { params }),
  },

  // Routes
  routes: {
    list: (params?: { page?: number; limit?: number }) => apiClient.get('/routes', { params }),
    get: (id: string) => apiClient.get(`/routes/${id}`),
    create: (data: unknown) => apiClient.post('/routes', data),
    optimize: (routeId: string) => apiClient.post(`/routes/${routeId}/optimize`),
  },

  // Dashboard
  dashboard: {
    getMetrics: (countries?: string[]) =>
      apiClient.get('/dashboard/metrics', { params: { countries: countries?.join(',') } }),
    getKPIs: (countries?: string[]) =>
      apiClient.get('/dashboard/kpis', { params: { countries: countries?.join(',') } }),
    getActivity: (limit?: number) => apiClient.get('/dashboard/activity', { params: { limit } }),
    getOverview: (countries?: string[]) =>
      apiClient.get('/dashboard/overview', { params: { countries: countries?.join(',') } }),
    getCountryData: (countryCode: string) => apiClient.get(`/dashboard/countries/${countryCode}`),
    getCountryComparison: (countries: string[]) =>
      apiClient.get('/dashboard/countries/compare', { params: { countries: countries.join(',') } }),
    // Real-time endpoints
    getPowerUnitLocations: (countries?: string[]) =>
      apiClient.get('/dashboard/realtime/power-units', {
        params: { countries: countries?.join(',') },
      }),
    getPowerUnitTracking: (powerUnitId: string) =>
      apiClient.get(`/dashboard/realtime/power-units/${powerUnitId}/tracking`),
  },

  // Finance
  finance: {
    invoices: {
      list: (params?: unknown) => apiClient.get('/invoices', { params }),
      get: (id: string) => apiClient.get(`/invoices/${id}`),
      create: (data: unknown) => apiClient.post('/invoices', data),
      update: (id: string, data: unknown) => apiClient.patch(`/invoices/${id}`, data),
      recordPayment: (id: string, data: unknown) =>
        apiClient.post(`/invoices/${id}/payments`, data),
    },
    expenses: {
      list: (params?: unknown) => apiClient.get('/expenses', { params }),
      get: (id: string) => apiClient.get(`/expenses/${id}`),
      create: (data: unknown) => apiClient.post('/expenses', data),
      update: (id: string, data: unknown) => apiClient.patch(`/expenses/${id}`, data),
    },
  },

  // Notifications
  notifications: {
    list: (params?: unknown) => apiClient.get('/notifications', { params }),
    markAsRead: (id: string) => apiClient.patch(`/notifications/${id}/read`),
    getPreferences: () => apiClient.get('/notifications/preferences'),
    updatePreferences: (data: unknown) => apiClient.patch('/notifications/preferences', data),
  },

  // Reports
  reports: {
    list: (params?: unknown) => apiClient.get('/reporting/reports', { params }),
    get: (id: string) => apiClient.get(`/reporting/reports/${id}`),
    create: (data: unknown) => apiClient.post('/reporting/reports', data),
    update: (id: string, data: unknown) => apiClient.patch(`/reporting/reports/${id}`, data),
    delete: (id: string) => apiClient.delete(`/reporting/reports/${id}`),
    execute: (id: string) => apiClient.post(`/reporting/reports/${id}/execute`),
    export: (id: string, format: string) =>
      apiClient.get(`/reporting/reports/${id}/export/${format}`, { responseType: 'blob' }),
    // Templates
    templates: {
      list: () => apiClient.get('/reporting/templates'),
      get: (id: string) => apiClient.get(`/reporting/templates/${id}`),
      getByCategory: (category: string) =>
        apiClient.get(`/reporting/templates/category/${category}`),
      create: (data: unknown) => apiClient.post('/reporting/templates', data),
      update: (id: string, data: unknown) => apiClient.patch(`/reporting/templates/${id}`, data),
      delete: (id: string) => apiClient.delete(`/reporting/templates/${id}`),
      createReport: (
        id: string,
        data: { name: string; description?: string; parameters?: Record<string, unknown> }
      ) => apiClient.post(`/reporting/templates/${id}/create-report`, data),
    },
  },

  // Schedules
  schedules: {
    list: (params?: {
      page?: number;
      limit?: number;
      status?: string;
      frequency?: string;
      reportId?: string;
      activeOnly?: boolean;
      search?: string;
    }) => apiClient.get('/reporting/schedules', { params }),
    get: (id: string) => apiClient.get(`/reporting/schedules/${id}`),
    create: (data: unknown) => apiClient.post('/reporting/schedules', data),
    update: (id: string, data: unknown) => apiClient.put(`/reporting/schedules/${id}`, data),
    delete: (id: string) => apiClient.delete(`/reporting/schedules/${id}`),
    pause: (id: string) => apiClient.post(`/reporting/schedules/${id}/pause`),
    resume: (id: string) => apiClient.post(`/reporting/schedules/${id}/resume`),
    trigger: (id: string, data?: unknown) =>
      apiClient.post(`/reporting/schedules/${id}/trigger`, data),
    stats: () => apiClient.get('/reporting/schedules/stats'),
    upcoming: (limit?: number) =>
      apiClient.get('/reporting/schedules/upcoming', { params: { limit } }),
    forReport: (reportId: string) => apiClient.get(`/reporting/schedules/report/${reportId}`),
  },

  // Schema Metadata (for Report Designer)
  schema: {
    getAvailableTables: (params?: { locale?: string; module?: string }) =>
      apiClient.get('/reporting/query-builder/available-tables', { params }),
    getTableInfo: (tableName: string, params?: { locale?: string }) =>
      apiClient.get(`/reporting/query-builder/available-tables/${tableName}`, { params }),
    getAvailableModules: () => apiClient.get('/reporting/query-builder/available-modules'),
  },
};

// Helper to create API endpoints
export function createApiEndpoint<TRequest = unknown, TResponse = unknown>(endpoint: string) {
  return {
    getAll: (params?: unknown) => api.get<TResponse[]>(endpoint, { params }),
    getById: (id: string | number) => api.get<TResponse>(`${endpoint}/${id}`),
    create: (data: TRequest) => api.post<TResponse>(endpoint, data),
    update: (id: string | number, data: Partial<TRequest>) =>
      api.patch<TResponse>(`${endpoint}/${id}`, data),
    delete: (id: string | number) => api.delete<void>(`${endpoint}/${id}`),
  };
}

// Example usage:
// const customersApi = createApiEndpoint<CustomerRequest, CustomerResponse>('/customers');
// const response = await customersApi.getAll({ page: 1, limit: 10 });
